// ============================================================
// APEX TRACKER - Client Riot API
// Appelle l'Edge Function proxy (la clé Riot est sécurisée côté serveur)
// ============================================================

import { supabase } from './supabase';

const FUNCTION_NAME = 'riot-proxy';

// Mapping des Data Dragon IDs vers les display names APEX
const CHAMPION_NAME_OVERRIDES = {
  'Kaisa': "Kai'Sa",
  'Khazix': "Kha'Zix",
  'Chogath': "Cho'Gath",
  'KogMaw': "Kog'Maw",
  'Belveth': "Bel'Veth",
  'Velkoz': "Vel'Koz",
  'RekSai': "Rek'Sai",
  'LeeSin': 'Lee Sin',
  'JarvanIV': 'Jarvan IV',
  'MasterYi': 'Master Yi',
  'MissFortune': 'Miss Fortune',
  'MonkeyKing': 'Wukong',
  'TahmKench': 'Tahm Kench',
  'TwistedFate': 'Twisted Fate',
  'XinZhao': 'Xin Zhao',
  'AurelionSol': 'Aurelion Sol',
  'DrMundo': 'Dr. Mundo',
  'KSante': "K'Sante",
  'Nunu': 'Nunu & Willump'
};

function displayName(championId) {
  return CHAMPION_NAME_OVERRIDES[championId] || championId;
}

async function invokeFunction(path, params) {
  const query = new URLSearchParams(params).toString();
  const { data, error } = await supabase.functions.invoke(`${FUNCTION_NAME}/${path}?${query}`, {
    method: 'GET'
  });
  if (error) throw new Error(error.message || 'Riot proxy error');
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Récupère le PUUID d'un compte à partir du Riot ID (gameName#tagLine)
 */
export async function getAccountByRiotId(gameName, tagLine, platform = 'europe') {
  return await invokeFunction('account', { gameName, tagLine, platform });
}

/**
 * Récupère la liste des match IDs récents d'un joueur
 * queue 420 = solo/duo ranked, 440 = flex
 */
export async function getMatchIds(puuid, { count = 20, start = 0, queue = 420, platform = 'europe', startTime } = {}) {
  const params = { puuid, count, start, queue, platform };
  if (startTime) params.startTime = startTime; // UNIX seconds
  return await invokeFunction('matches', params);
}

/**
 * Récupère les détails complets d'un match
 */
export async function getMatchDetails(matchId, platform = 'europe') {
  return await invokeFunction('match', { matchId, platform });
}

/**
 * Récupère la timeline d'un match (events minute par minute : kills, deaths, objectives...)
 */
export async function getMatchTimeline(matchId, platform = 'europe') {
  return await invokeFunction('timeline', { matchId, platform });
}

/**
 * Récupère les entrées de league d'un joueur (rang SoloQ, etc.)
 */
export async function getLeagueEntries(puuid, region = 'euw1') {
  return await invokeFunction('league', { puuid, region });
}

/**
 * V7.5.0 : Récupère les cutoffs Apex tiers (Master / GM / Challenger)
 * pour calculer la barre de progression dans le rank top tier.
 *
 * Endpoints Riot officiels :
 *   GET /lol/league/v4/masterleagues/by-queue/RANKED_SOLO_5x5
 *   GET /lol/league/v4/grandmasterleagues/by-queue/RANKED_SOLO_5x5
 *   GET /lol/league/v4/challengerleagues/by-queue/RANKED_SOLO_5x5
 *
 * Retourne un objet :
 *   {
 *     masterCount: 700,           // nombre de joueurs Master+
 *     gmCount: 200,               // nombre de GM+
 *     challengerCount: 50,        // nombre de Challenger
 *     masterToGmCutoff: 596,      // LP du dernier GM (cutoff pour rentrer en GM)
 *     gmToChallengerCutoff: 850,  // LP du dernier Challenger (cutoff pour rentrer en Challenger)
 *     challengerTopLp: 2143       // LP du #1 Challenger (objectif ultime "Road to Challenger")
 *   }
 *
 * Nécessite l'Edge Function Supabase à supporter le path 'apex-tiers'.
 * Si l'edge function n'est pas à jour, on retourne null et l'UI utilise un fallback.
 */
export async function getApexTiers(region = 'euw1') {
  try {
    return await invokeFunction('apex-tiers', { region });
  } catch (e) {
    console.warn('[getApexTiers] Edge function not available, returning null:', e);
    return null;
  }
}

/**
 * Détecte le contexte d'une mort à partir de la timeline
 * Retourne un texte descriptif (ex: "Solo bot lane après gank Lee Sin")
 */
function detectDeathContext(deathEvent, frame, allEvents, participants, myParticipantId, myTeamId) {
  const deathTime = deathEvent.timestamp;
  const deathPos = deathEvent.position || { x: 0, y: 0 };
  const minute = Math.floor(deathTime / 60000);

  // Killer info
  const killer = participants.find(p => p.participantId === deathEvent.killerId);
  const killerName = killer ? displayName(killer.championName) : 'unknown';
  const killerTeam = killer ? killer.teamId : null;

  // Trouver les events autour de la mort (±15 secondes pour TF, ±20s pour analyse trade)
  const windowStart = deathTime - 15000;
  const windowEnd = deathTime + 15000; // élargi pour capturer les morts post-trade
  const nearbyEvents = allEvents.filter(e => e.timestamp >= windowStart && e.timestamp <= windowEnd);

  // === V6.1 : Calcul du trade dans la fenêtre ===
  // Compter combien d'alliés / ennemis sont morts dans la fenêtre (hors moi)
  const killsInWindow = nearbyEvents.filter(e => e.type === 'CHAMPION_KILL');
  let alliesDiedInWindow = 0;
  let enemiesDiedInWindow = 0;
  killsInWindow.forEach(e => {
    if (e.victimId === myParticipantId) return; // c'est ma propre mort
    const victim = participants.find(p => p.participantId === e.victimId);
    if (!victim) return;
    if (victim.teamId === myTeamId) alliesDiedInWindow++;
    else enemiesDiedInWindow++;
  });

  // Le killer meurt-il aussi peu après (1v1 trade) ?
  const killerDiesInWindow = killsInWindow.some(e =>
    e.victimId === deathEvent.killerId && e.timestamp > deathTime && e.timestamp <= deathTime + 10000
  );

  // === V6.1 : Objectif pris pendant la fenêtre ?
  const objectiveEvent = nearbyEvents.find(e =>
    e.type === 'ELITE_MONSTER_KILL' || e.type === 'BUILDING_KILL'
  );
  // Pris par mon équipe ?
  const objectiveByMyTeam = objectiveEvent
    ? (objectiveEvent.killerTeamId === myTeamId || objectiveEvent.killerId
        ? participants.find(p => p.participantId === objectiveEvent.killerId)?.teamId === myTeamId
        : false)
    : false;

  // === Heuristiques existantes
  const isTeamfight = killsInWindow.length >= 3;
  const zone = detectMapZone(deathPos.x, deathPos.y);

  const assists = (deathEvent.assistingParticipantIds || []).map(id => participants.find(p => p.participantId === id)).filter(Boolean);
  const enemyJunglerAssist = assists.find(p => p.teamPosition === 'JUNGLE');
  const isGank = enemyJunglerAssist && zone !== 'jungle' && minute < 25;

  // Construction du contexte (texte descriptif)
  let context = '';
  if (isTeamfight && objectiveEvent) {
    const obj = objectiveEvent.monsterType || objectiveEvent.buildingType || 'objectif';
    context = `TF ${obj.toLowerCase()} à ${zone} (${minute}min) — ${killerName}`;
  } else if (isTeamfight) {
    context = `TF en ${zone} (${minute}min) — ${killerName}`;
  } else if (isGank) {
    context = `Gank ${zone} par ${killerName} + ${enemyJunglerAssist.championName} (${minute}min)`;
  } else if (assists.length === 0) {
    context = `Solo ${zone} (${minute}min) — ${killerName}`;
  } else {
    context = `${zone} ${minute}min, killed by ${killerName} +${assists.length}`;
  }

  return {
    context,
    position: deathPos,
    killerName,
    isGank,
    isTeamfight,
    // V6.1 : nouvelles données pour auto-classification
    alliesDiedInWindow,
    enemiesDiedInWindow,
    killerDiesInWindow,
    objectiveByMyTeam,
    objectiveEvent: !!objectiveEvent,
    isSoloDeath: assists.length === 0 && !isTeamfight,
    zone,
    minute
  };
}

/**
 * Détermine la zone de la map à partir des coordonnées
 * Map size: 14870 x 14980
 */
function detectMapZone(x, y) {
  // River (zone centrale)
  const isRiver = Math.abs(x - y) < 2500 && x > 3000 && x < 12000;

  // Bot lane : ligne sud (x élevé, y bas) ou ligne nord-est étroite
  const isBotLane = (x > 9000 && y < 5500) || (x > 11500 && y < 8000);
  // Top lane : ligne nord (x bas, y élevé)
  const isTopLane = (x < 5500 && y > 9000) || (x < 8000 && y > 11500);
  // Mid lane : diagonale centrale
  const isMidLane = Math.abs(x - y) < 1500 && x > 4000 && x < 11000;

  // Bases
  const isAllyBase = x < 4000 && y < 4000;
  const isEnemyBase = x > 11000 && y > 11000;

  if (isAllyBase) return 'base alliée';
  if (isEnemyBase) return 'base ennemie';
  if (isBotLane) return 'bot';
  if (isTopLane) return 'top';
  if (isMidLane) return 'mid';
  if (isRiver) return 'river';
  return 'jungle';
}

/**
 * V6.1 — Calcule la Net Value d'une mort (gold/xp net pour le team)
 *
 * Coûts directs :
 *   - Bounty donné à l'ennemi (depuis l'event KILL)
 *   - Gold non-farmé pendant le respawn (~25g/CS sur la durée respawn)
 *   - XP non-gagné en lane (~30 XP/wave manqué)
 * Coûts d'opportunité :
 *   - Objectifs pris par l'ennemi pendant le respawn
 *   - Towers prises par l'ennemi pendant le respawn
 * Bénéfices (si investment) :
 *   - Gold gagné par les alliés pendant ton respawn (kills sur ennemis)
 *   - Objectifs pris par ta team pendant ton respawn
 *
 * @returns {object} { netValue: number, costs: {...}, gains: {...} }
 */
function calculateDeathNetValue(deathEvent, allEvents, participants, myParticipantId, myTeamId, myLevel) {
  const deathTime = deathEvent.timestamp;

  // === Estimation du temps de respawn (formule LoL approx) ===
  // Base respawn time: BRW (Base Respawn Wait) selon le niveau du joueur
  // À partir du level 7+: BRW = 2 * level + 4 (approximatif)
  // À partir du level 9+: BRW = level * 2.5 + 7.5
  // Formule simplifiée : ~10 + level * 2 secondes (jusqu'à level 30+)
  const level = myLevel || 6;
  const respawnSec = level <= 6 ? 8 + level * 1.2 : 10 + level * 2;
  const respawnMs = respawnSec * 1000;
  const respawnEnd = deathTime + respawnMs;

  // === Coût direct : bounty donné ===
  const bountyGiven = deathEvent.bounty || deathEvent.shutdownBounty || 300; // valeur par défaut si absent

  // === Coût direct : gold/xp non farmé ===
  // Estimation : pendant respawn + walk back (~10s), tu rates ~1 wave (~6 minions = 150g, 90xp)
  const minionsLost = Math.round((respawnSec + 10) / 30); // 1 wave toutes les 30s
  const goldNotFarmed = minionsLost * 22; // ~22g par minion ranged moyen
  const xpNotGained = minionsLost * 35; // ~35 XP par minion

  // === Events pendant le respawn (contexte) ===
  const eventsDuringRespawn = allEvents.filter(e =>
    e.timestamp > deathTime && e.timestamp <= respawnEnd
  );

  // === Coût d'opportunité : objectifs/towers perdus ===
  let objectivesLostGold = 0;
  let towersLostGold = 0;
  // === Bénéfices : objectifs/towers gagnés par ta team ===
  let objectivesGainedGold = 0;
  let teamKillsGold = 0;

  eventsDuringRespawn.forEach(e => {
    if (e.type === 'ELITE_MONSTER_KILL') {
      // Drake = 200g par joueur, Herald = ~125g, Baron = 300g, Atakhan = 250g
      let goldValue = 200;
      if (e.monsterType === 'BARON_NASHOR') goldValue = 300;
      else if (e.monsterType === 'RIFTHERALD') goldValue = 125;
      else if (e.monsterType === 'DRAGON') goldValue = 200;
      else if (e.monsterType === 'HORDE') goldValue = 100; // Voidgrubs

      const killerTeam = participants.find(p => p.participantId === e.killerId)?.teamId;
      if (killerTeam === myTeamId) objectivesGainedGold += goldValue;
      else if (killerTeam) objectivesLostGold += goldValue;
    } else if (e.type === 'BUILDING_KILL') {
      // Tower = 250-400g local + global gold à split
      const killerTeam = e.killerTeamId || participants.find(p => p.participantId === e.killerId)?.teamId;
      const goldValue = e.buildingType === 'INHIBITOR_BUILDING' ? 400 : 250;
      if (killerTeam === myTeamId) objectivesGainedGold += goldValue;
      else if (killerTeam) towersLostGold += goldValue;
    } else if (e.type === 'CHAMPION_KILL') {
      // Kills par mes alliés sur ennemis = bénéfice (estimé 300g moyen)
      const killer = participants.find(p => p.participantId === e.killerId);
      const victim = participants.find(p => p.participantId === e.victimId);
      if (killer && victim && killer.teamId === myTeamId && victim.teamId !== myTeamId) {
        teamKillsGold += (e.bounty || 300);
      }
    }
  });

  // === Calcul net ===
  const totalCost = bountyGiven + goldNotFarmed + objectivesLostGold + towersLostGold;
  const totalGain = teamKillsGold + objectivesGainedGold;
  const netValue = totalGain - totalCost;

  return {
    netValue,
    respawnSec: Math.round(respawnSec),
    costs: {
      bountyGiven,
      goldNotFarmed,
      xpNotGained,
      objectivesLost: objectivesLostGold,
      towersLost: towersLostGold,
      total: totalCost
    },
    gains: {
      teamKills: teamKillsGold,
      objectivesGained: objectivesGainedGold,
      total: totalGain
    }
  };
}

/**
 * V6.1 — Suggère un type de mort basé sur le contexte enrichi
 * Heuristiques :
 *   INVESTMENT : ta mort = trade gagnant pour la team
 *     - Au moins 2 ennemis morts dans la fenêtre
 *     - OU : ton killer meurt aussi dans les 10s (1v1 trade)
 *     - OU : objectif pris par ta team pendant la fenêtre
 *     - OU : team ace (4-5 ennemis morts dans la fenêtre)
 *   GREED : tu meurs sans contrepartie
 *     - Mort solo, aucun autre kill team dans la fenêtre
 *     - OU : mort solo après 25min (push trop loin late)
 *   POSITION : trade équilibré
 *     - Trade ~équilibré dans une teamfight (1-1, 2-2)
 *     - Default cas
 */
function suggestDeathType(detection, minute) {
  const { alliesDiedInWindow, enemiesDiedInWindow, killerDiesInWindow, objectiveByMyTeam, isSoloDeath, isTeamfight, zone } = detection;

  // === INVESTMENT cases ===
  // Team ace en cours (4+ ennemis morts) → ta mort a permis ça
  if (enemiesDiedInWindow >= 4) return 'investment';
  // Trade très favorable (2+ ennemis morts pour ≤ 1 allié + toi)
  if (enemiesDiedInWindow >= 2 && alliesDiedInWindow <= 1) return 'investment';
  // 1v1 où ton killer meurt aussi (tu as servi à le killer ensuite)
  if (killerDiesInWindow && enemiesDiedInWindow === 0 && alliesDiedInWindow === 0) return 'investment';
  if (killerDiesInWindow && enemiesDiedInWindow >= 1) return 'investment';
  // Objectif pris par ta team pendant ton respawn (tu as buy time)
  if (objectiveByMyTeam) return 'investment';

  // === GREED cases ===
  // Mort solo sans aucun trade
  if (isSoloDeath && enemiesDiedInWindow === 0 && alliesDiedInWindow === 0) {
    return 'greed';
  }
  // Mort solo en lane vide late game
  if (minute > 25 && isSoloDeath && (zone === 'top' || zone === 'bot' || zone === 'mid')) {
    return 'greed';
  }

  // === POSITION cases ===
  // Trade équilibré en TF
  if (isTeamfight) return 'position';
  // Default
  return 'position';
}

/**
 * Parse un match Riot + sa timeline vers le format APEX game
 *
 * @param {object} match - Réponse de getMatchDetails
 * @param {object} timeline - Réponse de getMatchTimeline
 * @param {string} puuid - PUUID de l'utilisateur pour identifier son joueur
 * @returns {object} - Game au format APEX (champs compatibles avec le form)
 */
export function parseMatchToApexGame(match, timeline, puuid) {
  const info = match.info;
  const participants = info.participants;

  // Trouver le joueur cible
  const me = participants.find(p => p.puuid === puuid);
  if (!me) throw new Error('Joueur non trouvé dans le match');

  const myTeam = me.teamId; // 100 ou 200
  const enemyTeam = myTeam === 100 ? 200 : 100;

  // Durée en minutes (affiché dans l'UI) + secondes brutes (stockées pour calculs précis)
  const durationSeconds = info.gameDuration || 0; // secondes brutes Riot API
  const duration = Math.round(durationSeconds / 60);

  // Composition équipes
  const allies = participants.filter(p => p.teamId === myTeam);
  const enemies = participants.filter(p => p.teamId === enemyTeam);

  const mySupport = allies.find(p => p.teamPosition === 'UTILITY');
  const myJungler = allies.find(p => p.teamPosition === 'JUNGLE');
  const myAdcP    = allies.find(p => p.teamPosition === 'BOTTOM');   // pour le Support
  const myTopP    = allies.find(p => p.teamPosition === 'TOP');      // pour le Jungle
  const enemyAdcP     = enemies.find(p => p.teamPosition === 'BOTTOM');
  const enemySupportP = enemies.find(p => p.teamPosition === 'UTILITY');
  const enemyJunglerP = enemies.find(p => p.teamPosition === 'JUNGLE');
  const enemyMidP     = enemies.find(p => p.teamPosition === 'MIDDLE');
  const enemyTopP     = enemies.find(p => p.teamPosition === 'TOP');

  // Damage total de l'équipe alliée
  const teamDamage = allies.reduce((sum, p) => sum + (p.totalDamageDealtToChampions || 0), 0);

  // CS par minute
  const totalCs = (me.totalMinionsKilled || 0) + (me.neutralMinionsKilled || 0);
  const csPerMin = duration > 0 ? +(totalCs / duration).toFixed(1) : 0;

  // ============================================================
  // STATS OPPONENT (ADC adverse)
  // ============================================================
  // ============================================================
  // Adversaire de lane selon le rôle joué (v8.22)
  // Avant : toujours enemyAdcP. Maintenant : bon laner adverse.
  // ============================================================
  const POS_TO_LANE_ENEMY = {
    'BOTTOM':  enemyAdcP,
    'UTILITY': enemySupportP,
    'MIDDLE':  enemyMidP,
    'TOP':     enemyTopP,
    'JUNGLE':  enemyJunglerP,
  };
  const laneOpponent = POS_TO_LANE_ENEMY[me.teamPosition] || enemyAdcP;

  let opponentStats = null;
  let timeFrames = null;
  if (laneOpponent) {
    const oppCs = (laneOpponent.totalMinionsKilled || 0) + (laneOpponent.neutralMinionsKilled || 0);

    // Kills directs sur l'adversaire de lane
    let killsOnOpponent = 0;
    let deathsToOpponent = 0;
    if (timeline?.info?.frames) {
      timeline.info.frames.forEach(frame => {
        if (!frame.events) return;
        frame.events.forEach(ev => {
          if (ev.type !== 'CHAMPION_KILL') return;
          if (ev.killerId === me.participantId && ev.victimId === laneOpponent.participantId) {
            killsOnOpponent++;
          }
          if (ev.victimId === me.participantId && ev.killerId === laneOpponent.participantId) {
            deathsToOpponent++;
          }
        });
      });
    }

    opponentStats = {
      champion: displayName(laneOpponent.championName),
      kills: laneOpponent.kills,
      deaths: laneOpponent.deaths,
      assists: laneOpponent.assists,
      cs: oppCs,
      csPerMin: duration > 0 ? +(oppCs / duration).toFixed(1) : 0,
      damageDealt: laneOpponent.totalDamageDealtToChampions || 0,
      damagePerMin: duration > 0 ? Math.round((laneOpponent.totalDamageDealtToChampions || 0) / duration) : 0,
      goldEarned: laneOpponent.goldEarned || 0,
      visionScore: laneOpponent.visionScore || 0,
      killsOnMe: deathsToOpponent,
      killsOfMe: killsOnOpponent
    };

    // ============================================================
    // TIME FRAMES (5/10/15/20min) — Gold/CS/XP/Level vs lane opponent
    // ============================================================
    if (timeline?.info?.frames) {
      const timestamps = [5, 10, 15, 20, 25, 30];
      timeFrames = {};
      timestamps.forEach(min => {
        const targetMs = min * 60000;
        const frame = timeline.info.frames.find(f => f.timestamp >= targetMs && f.timestamp < targetMs + 60000)
                  || timeline.info.frames[Math.min(min, timeline.info.frames.length - 1)];
        if (!frame || !frame.participantFrames) return;

        const myFrame  = frame.participantFrames[me.participantId];
        const oppFrame = frame.participantFrames[laneOpponent.participantId];
        if (!myFrame || !oppFrame) return;

        timeFrames[`min${min}`] = {
          minute: min,
          my: {
            cs:    (myFrame.minionsKilled || 0) + (myFrame.jungleMinionsKilled || 0),
            gold:  myFrame.totalGold || 0,
            xp:    myFrame.xp || 0,
            level: myFrame.level || 0
          },
          opp: {
            cs:    (oppFrame.minionsKilled || 0) + (oppFrame.jungleMinionsKilled || 0),
            gold:  oppFrame.totalGold || 0,
            xp:    oppFrame.xp || 0,
            level: oppFrame.level || 0
          }
        };
      });
    }
  }

  // ============================================================
  // MORTS avec coordonnées + détection phase + suggestion type
  // ============================================================
  const myParticipantId = me.participantId;
  const deathDetails = [];

  if (timeline?.info?.frames) {
    // Collecter tous les events pour l'analyse contextuelle
    const allEvents = timeline.info.frames.flatMap(f => f.events || []);

    timeline.info.frames.forEach(frame => {
      if (!frame.events) return;
      frame.events.forEach(ev => {
        if (ev.type === 'CHAMPION_KILL' && ev.victimId === myParticipantId) {
          const minute = Math.floor(ev.timestamp / 60000);
          const second = Math.floor((ev.timestamp % 60000) / 1000);

          // Detection
          const detection = detectDeathContext(ev, frame, allEvents, participants, myParticipantId, myTeam);
          const suggestedType = suggestDeathType(detection, minute);

          // V6.1 : Calcul Death Net Value
          // On a besoin du level du joueur au moment de la mort
          const myFrameAtDeath = frame.participantFrames?.[myParticipantId];
          const myLevelAtDeath = myFrameAtDeath?.level || Math.min(6 + Math.floor(minute / 3), 18);
          const netValue = calculateDeathNetValue(ev, allEvents, participants, myParticipantId, myTeam, myLevelAtDeath);

          deathDetails.push({
            id: Date.now() + Math.random(),
            minute,
            second,
            type: suggestedType,
            context: detection.context,
            position: detection.position, // {x, y}
            killer: detection.killerName,
            // V6.1 : Net Value
            netValue: netValue.netValue,
            costs: netValue.costs,
            gains: netValue.gains,
            respawnSec: netValue.respawnSec,
            myLevel: myLevelAtDeath,
            _auto: true,
            _categorized: false // flag pour bulk categorizer
          });
        }
      });
    });
  }

  // ============================================================
  // V7.7.2 : Extraction events objectifs + morts alliés (pour Game-level Insights A1/A2/A4 + B)
  // ============================================================
  const objectiveEvents = []; // { type, subType, minute, second, teamId, isMyTeam }
  const allyDeaths = [];      // morts des alliés (hors moi) : { minute, second, role, participantId }
  const myDeathTimeline = []; // mes morts au format simple { minute, second } pour crosscheck
  const itemEvents = [];      // V7.7.3 : { action:'purchase'|'sell', itemId, minute, second } (moi)
  const wardEvents = [];      // V7.7.3 : { action:'placed'|'killed', wardType, minute, second } (moi)
  const levelEvents = [];     // V7.7.3 : { level, minute, second } (moi)
  const oppLevelEvents = [];  // V7.7.3 : niveaux du lane opponent (pour spike E1/E2/E3)

  if (timeline?.info?.frames) {
    const myTeamId = myTeam; // 100 ou 200
    const oppAdcPid = enemyAdcP?.participantId || null;

    timeline.info.frames.forEach(frame => {
      if (!frame.events) return;
      frame.events.forEach(ev => {
        const tMs = ev.timestamp || 0;
        const minute = Math.floor(tMs / 60000);
        const second = Math.floor((tMs % 60000) / 1000);

        // V7.7.3 : items achetés/vendus (moi)
        if (ev.type === 'ITEM_PURCHASED' && ev.participantId === me.participantId) {
          itemEvents.push({ action: 'purchase', itemId: ev.itemId, minute, second });
        }
        if (ev.type === 'ITEM_SOLD' && ev.participantId === me.participantId) {
          itemEvents.push({ action: 'sell', itemId: ev.itemId, minute, second });
        }

        // V7.7.3 : wards placées/tuées (moi)
        if (ev.type === 'WARD_PLACED' && ev.creatorId === me.participantId) {
          wardEvents.push({ action: 'placed', wardType: ev.wardType || 'UNKNOWN', minute, second });
        }
        if (ev.type === 'WARD_KILL' && ev.killerId === me.participantId) {
          wardEvents.push({ action: 'killed', wardType: ev.wardType || 'UNKNOWN', minute, second });
        }

        // V7.7.3 : level up (moi + opp lane)
        if (ev.type === 'LEVEL_UP') {
          if (ev.participantId === me.participantId) {
            levelEvents.push({ level: ev.level, minute, second });
          } else if (oppAdcPid && ev.participantId === oppAdcPid) {
            oppLevelEvents.push({ level: ev.level, minute, second });
          }
        }
      });
    });

    timeline.info.frames.forEach(frame => {
      if (!frame.events) return;
      frame.events.forEach(ev => {
        const tMs = ev.timestamp || 0;
        const minute = Math.floor(tMs / 60000);
        const second = Math.floor((tMs % 60000) / 1000);

        // Events objectifs (ELITE_MONSTER_KILL = dragon/baron/herald/voidgrubs en 2026)
        if (ev.type === 'ELITE_MONSTER_KILL') {
          const killerTeam = participants.find(p => p.participantId === ev.killerId)?.teamId;
          // monsterType : DRAGON, RIFTHERALD, BARON_NASHOR, HORDE (voidgrubs 2026)
          objectiveEvents.push({
            type: 'monster',
            subType: ev.monsterType || 'UNKNOWN',
            subSubType: ev.monsterSubType || null, // ex: type de dragon (FIRE_DRAGON, etc.)
            minute,
            second,
            teamId: killerTeam,
            isMyTeam: killerTeam === myTeamId
          });
        }

        // Building kills (tours/inhibs) pour pattern B6 (tour offerte)
        if (ev.type === 'BUILDING_KILL') {
          const killerTeam = ev.killerTeamId ||
            participants.find(p => p.participantId === ev.killerId)?.teamId;
          objectiveEvents.push({
            type: 'building',
            subType: ev.buildingType || 'TOWER', // TOWER_BUILDING, INHIBITOR_BUILDING
            towerType: ev.towerType || null,     // OUTER_TURRET, INNER_TURRET, etc.
            laneType: ev.laneType || null,       // TOP_LANE, MID_LANE, BOT_LANE
            minute,
            second,
            teamId: killerTeam,
            isMyTeam: killerTeam === myTeamId
          });
        }

        // Morts de champions : on classe entre moi / alliés
        if (ev.type === 'CHAMPION_KILL') {
          if (ev.victimId === me.participantId) {
            myDeathTimeline.push({ minute, second, ts: tMs });
          } else {
            const victim = participants.find(p => p.participantId === ev.victimId);
            if (victim && victim.teamId === myTeamId) {
              // Allié mort (pas moi)
              const victimP = info.participants?.find(p => p.participantId === ev.victimId);
              allyDeaths.push({
                minute,
                second,
                ts: tMs,
                participantId: ev.victimId,
                role: victimP?.teamPosition || 'UNKNOWN',
                isJungler: victimP?.teamPosition === 'JUNGLE'
              });
            }
          }
        }
      });
    });
  }

  // ============================================================
  // Stats supplémentaires utiles
  // ============================================================
  const myStats = {
    visionScore: me.visionScore || 0,
    goldEarned: me.goldEarned || 0,
    largestKillingSpree: me.largestKillingSpree || 0,
    largestMultiKill: me.largestMultiKill || 0,
    wardsPlaced: me.wardsPlaced || 0,
    wardsKilled: me.wardsKilled || 0
  };

  return {
    // Identifiant pour déduplication
    riot_match_id: match.metadata.matchId,

    // Composition
    champion: displayName(me.championName),
    support: mySupport ? displayName(mySupport.championName) : 'Nautilus',
    allyAdc: myAdcP ? displayName(myAdcP.championName) : null,         // ADC allié (utile pour Support)
    allyTop: myTopP ? displayName(myTopP.championName) : null,         // Top allié (utile pour Jungle)
    enemyAdc: enemyAdcP ? displayName(enemyAdcP.championName) : 'Caitlyn',
    enemySupport: enemySupportP ? displayName(enemySupportP.championName) : 'Nautilus',
    enemyMid: enemyMidP ? displayName(enemyMidP.championName) : null,
    enemyTop: enemyTopP ? displayName(enemyTopP.championName) : null,
    jungler: myJungler ? displayName(myJungler.championName) : 'Graves',
    enemyJungler: enemyJunglerP ? displayName(enemyJunglerP.championName) : 'Graves',

    // Résultat
    result: me.win ? 'W' : 'L',
    duration,
    // v8.0 : durée précise en secondes + timestamp Unix de début
    durationSeconds,
    startTime: info.gameStartTimestamp || info.gameCreation || null,
    // v8.4 : queueId pour filtres Solo/Flex/Normal (420=solo, 440=flex, 400/430=normal)
    queue_id: info.queueId || null,

    // KDA
    kills: me.kills,
    deaths: me.deaths,
    assists: me.assists,

    // Stats
    csPerMin,
    damageDealt: me.totalDamageDealtToChampions || 0,
    teamDamage,

    // Stats avancées (V5)
    myStats,
    opponentStats,
    timeFrames,

    // V6.3 : Side (Blue=100, Red=200) pour analyses contextuelles
    mySide: myTeam === 100 ? 'blue' : 'red',

    // V6.5 : Patch extrait de gameVersion (format "16.10.687.4567" → "16.10")
    // Note : la saison 2026 utilise le préfixe gameVersion "16.x" côté Riot.
    patch: (() => {
      const v = info.gameVersion || '';
      const parts = v.split('.');
      if (parts.length < 2) return null;
      return `${parts[0]}.${String(parts[1]).padStart(2, '0')}`;
    })(),

    // V6.6 : Détection autofill via teamPosition Riot API
    // BOTTOM = rôle ADC principal · autre = autofill
    role: (() => {
      const pos = me.teamPosition || '';
      const map = {
        'BOTTOM': 'ADC',
        'UTILITY': 'SUP',
        'MIDDLE': 'MID',
        'TOP': 'TOP',
        'JUNGLE': 'JG'
      };
      return map[pos] || 'UNKNOWN';
    })(),
    isAutofill: (me.teamPosition || 'BOTTOM') !== 'BOTTOM',

    // V7.5.1 : Game annulée si gameEndedInEarlySurrender (Riot flag officiel)
    // Marque les vrais remakes (déco < 3min30 d'un joueur, ratifié par le reste de l'équipe)
    isRemake: !!me.gameEndedInEarlySurrender,

    // Morts avec timestamps + coordonnées + détection
    deathDetails,

    // V7.7.2 : Events objectifs + morts alliés (pour Game-level Insights A1/A2/A4 + B1-B8)
    objectiveEvents,
    allyDeaths,
    myDeathTimeline,

    // V7.7.3 : Events items/wards/levels (pour Game-level Insights C/D/E)
    itemEvents,
    wardEvents,
    levelEvents,
    oppLevelEvents,

    // v7.2.4 : Items finaux étendus (slots 0..9 pour gérer le Role Quest slot 2026)
    // Historiquement : 0..5 = build, 6 = trinket
    // v26.01 (saison 2026) : possible nouveau slot 7 pour Role Quest (bottes ADC)
    // On récupère tout pour ne rien manquer.
    items: (() => {
      const all = [];
      for (let i = 0; i <= 9; i++) {
        all.push(me[`item${i}`] || 0);
      }
      return all;
    })(),

    // v7.2 : Runes (primaire/secondaire/shards)
    runes: extractRunes(me),

    // Qualitatif (défauts — l'utilisateur ajoute)
    keyMoments: '',
    feeling: 7,
    mental: 'Focus',

    // Meta
    timestamp: new Date(info.gameCreation).toISOString(),
    _source: 'riot_api'
  };
}

/**
 * Extrait le matchId depuis une URL OP.GG
 * Les URLs OP.GG contiennent une ID encodée qui n'est pas directement le matchId Riot.
 * Cette fonction tente de l'extraire mais ce n'est pas fiable à 100%.
 *
 * Alternative : demander à l'user de donner juste son Riot ID et charger les derniers matches.
 */
export function tryExtractMatchIdFromOpGg(url) {
  try {
    const u = new URL(url);
    // Pattern: /matches/{encoded}/{timestamp}
    const match = u.pathname.match(/\/matches\/([^/]+)\/(\d+)/);
    if (match) {
      return { encoded: match[1], timestamp: +match[2] };
    }
  } catch (e) {
    return null;
  }
  return null;
}

/**
 * Format du match ID Riot : EUW1_1234567890
 * Si l'user donne juste un nombre (ex: 1776895343000 de l'URL OP.GG), on peut essayer
 * de deviner le format mais ça reste peu fiable.
 *
 * Approche recommandée : charger les 20 derniers matches et laisser l'user choisir.
 */

/**
 * v7.2 — Extrait les runes d'un participant Riot.
 *
 * Format de retour stable indépendant de l'API Riot, prêt à stocker en DB.
 * Si perks est manquant, retourne null.
 *
 * Format Riot perks:
 *   perks.styles[0] = primary style (avec selections[0] = keystone)
 *   perks.styles[1] = secondary style
 *   perks.statPerks = { offense, flex, defense }
 */
function extractRunes(participant) {
  if (!participant?.perks?.styles) return null;
  const styles = participant.perks.styles;
  const primary = styles.find(s => s.description === 'primaryStyle');
  const secondary = styles.find(s => s.description === 'subStyle');
  const statPerks = participant.perks.statPerks || {};

  return {
    primaryStyle: primary?.style || null,
    secondaryStyle: secondary?.style || null,
    keystone: primary?.selections?.[0]?.perk || null,
    primary: (primary?.selections || []).map(s => s.perk).filter(Boolean),
    secondary: (secondary?.selections || []).map(s => s.perk).filter(Boolean),
    shards: [
      statPerks.offense,
      statPerks.flex,
      statPerks.defense
    ].filter(v => v != null)
  };
}

